import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import './ToggleSwitch.css';
import { useFileContext } from '../../ContextAPI/FileContext';
import { ManagerlApi, baseApi } from '../../API/api';


const AutomaticReportsTab = () => {
  const API = ManagerlApi;

  // ===================== 1) JSON Drive States ===================== //
  const [jsonDriveId, setJsonDriveId] = useState('0ACyc1j4NFP2PUk9PVA');
  const [jsonFolderId, setJsonFolderId] = useState('1WFxbkoJj9VTA5keSPqzHi3Twdqm3sAjy');
  const [availableJsonFiles, setAvailableJsonFiles] = useState([]);
  const [selectedJsonFileId, setSelectedJsonFileId] = useState('');
  const [adjustedJson, setAdjustedJson] = useState({});
  const [jsonLoadMessage, setJsonLoadMessage] = useState('');

  // ===================== 2) DOCX Template States ===================== //
  // We allow only one DOCX template.
  const [shared_drive_id, setshared_drive_id] = useState('0ACyc1j4NFP2PUk9PVA');
  const [template_folder_id, settemplate_folder_id] = useState('1JC89Xu80ItTwW4clJEHiKiX3nQekO0ud');
  const [availableDocxFiles, setAvailableDocxFiles] = useState([]);
  // Store the selected DOCX template as an object:
  // { fileId, fileName, references: [ { mongoType, mongoValue }, ... ] }
  const [docxTemplate, setDocxTemplate] = useState(null);

  // For adding new MongoDB references for the DOCX template:
  const [mongoType, setMongoType] = useState('');
  const [mongoValue, setMongoValue] = useState('');

  // ===================== 3) Other Form States ===================== //
  const [linkToFolder, setLinkToFolder] = useState('');
  const [output_drive_folder_id, setOutput_drive_folder_id] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [roadNumber, setRoadNumber] = useState('');
  const [startMile, setStartMile] = useState('');
  const [endMile, setEndMile] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [corridor, setCorridor] = useState('');
  const [pathCollection, setPathCollection] = useState(''); 

  // ===================== 4) Adjusted JSON for non-DOCX configuration ===================== //
  // (This comes from the loaded JSON file.)
  
  // ===================== 5) Request Tracking ===================== //
  const [requests, setRequests] = useState([]);

  // ===================== 6) Toggles ===================== //
  const [showAdjustFields, setShowAdjustFields] = useState(false);
  const [showAdjustTable, setShowAdjustTable] = useState(false);


  // ===================== JSON Fetching ===================== //
  const handleFetchJsonFiles = async () => {
    if (!jsonDriveId || !jsonFolderId) return;
    try {
      const resp = await axios.get(`${API}/api/list-files`, {
        params: {
          driveId: jsonDriveId,
          folderId: jsonFolderId,
          mimeType: 'application/json',
        },
      });
      setAvailableJsonFiles(resp.data.files || []);
    } catch (error) {
      console.error('Error fetching JSON files:', error);
    }
  };

  const handleLoadSelectedJson = async () => {
    if (!selectedJsonFileId) return;
    try {
      const resp = await axios.get(`${API}/get-file-content`, {
        params: { fileId: selectedJsonFileId },
      });
      const fileContent = resp.data;
      setAdjustedJson(fileContent);
      setJsonLoadMessage('JSON file loaded successfully!');
      setTimeout(() => {
        setJsonLoadMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error fetching JSON content:', error);
    }
  };

  // ===================== DOCX Fetching ===================== //
  const handleFetchDocxFiles = async () => {
    if (!shared_drive_id || !template_folder_id) return;
    try {
      const resp = await axios.get(`${API}/api/list-files`, {
        params: {
          driveId: shared_drive_id,
          folderId: template_folder_id,
          mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        },
      });
      setAvailableDocxFiles(resp.data.files || []);
    } catch (error) {
      console.error('Error fetching DOCX files:', error);
    }
  };

  // ===================== DOCX Template Selection ===================== //
  const handleSelectDocxTemplate = (fileId) => {
    if (!fileId) return;
    const found = availableDocxFiles.find((f) => f.id === fileId);
    if (!found) return;
    // Only one template allowed; overwrite any previous one.
    setDocxTemplate({
      fileId: found.id,
      fileName: found.name,
      references: []
    });
  };

  const handleRemoveTemplate = () => {
    setDocxTemplate(null);
  };

  // ===================== Adding MongoDB References ===================== //
  const handleAddReference = () => {
    if (!docxTemplate) return;
    if (!mongoType) return;
    // Append the new reference to the existing ones
    setDocxTemplate((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        references: [...prev.references, { mongoType, mongoValue }]
      };
    });
    // Clear the input fields
    setMongoType('');
    setMongoValue('');
  };

  const handleRemoveReference = (index) => {
    if (!docxTemplate) return;
    setDocxTemplate((prev) => {
      if (!prev) return null;
      const newRefs = prev.references.filter((_, i) => i !== index);
      return { ...prev, references: newRefs };
    });
  };

  // ===================== Adjusting JSON Fields ===================== //
  const handleFieldChange = (key, newValue) => {
    setAdjustedJson((prev) => {
      const updated = { ...prev };
      if (key.toLowerCase().endsWith('field name')) {
        const oldFieldName = prev[key];
        const newFieldName = newValue;
        updated[key] = newFieldName;
        if (!oldFieldName || !newFieldName) return updated;
        const prefix = key.replace(/field name$/i, '').trim();
        const suffixesToRename = ['value', 'color'];
        suffixesToRename.forEach((suffix) => {
          const oldKey = `${prefix} ${oldFieldName} ${suffix}`.trim();
          const newKey = `${prefix} ${newFieldName} ${suffix}`.trim();
          if (typeof updated[oldKey] !== 'undefined') {
            updated[newKey] = updated[oldKey];
            delete updated[oldKey];
          }
        });
        return updated;
      } else {
        updated[key] = newValue;
        return updated;
      }
    });
  };

  // Group table fields for display
  const groupedTableFields = {};
  Object.entries(adjustedJson).forEach(([key, value]) => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('table')) {
      const match = key.match(/^(.*?\btable\b)/i);
      if (match && match[1]) {
        const tableLabel = match[1].trim();
        if (!groupedTableFields[tableLabel]) {
          groupedTableFields[tableLabel] = [];
        }
        groupedTableFields[tableLabel].push({ key, value });
      }
    }
  });

  // ===================== Running the Report ===================== //
  const handleRunAutomaticReports = async () => {
    const apiUrl = `${API}/Automatic-Report`;

    // Flatten DOCX template references into top-level keys
    let docxPayload = {};
    if (docxTemplate) {
      docxPayload.fileId = docxTemplate.fileId;
      docxPayload.fileName = docxTemplate.fileName;
      docxTemplate.references.forEach((ref) => {
        if (ref.mongoType && ref.mongoValue) {
          docxPayload[ref.mongoType] = ref.mongoValue;
        }
      });
    }

     // Build project_name using fileName, State, and Road Number
    const fileNamePart = docxTemplate ? docxTemplate.fileName.replace(/\.docx$/i, '') : 'template';
    const project_name = `${fileNamePart}_${stateVal}_${roadNumber}`;

    // Build final payload merging JSON config, DOCX payload, and other fields.
    const requestData = {
      ...adjustedJson,
      "replace_link to Shapefile Folder": linkToFolder,
      output_drive_folder_id,
      State: stateVal,
      "Road Number": roadNumber,
      "Start Mile": startMile,
      "End Mile": endMile,
      "Job Number": jobNumber,
      Corridor: corridor,
      jsonDriveId,
      jsonFolderId,
      selectedJsonFileId,
      shared_drive_id,
      template_folder_id,
      ...docxPayload,
      project_name,
      ...(pathCollection ? { path_collection: pathCollection } : {}) // ← Include conditionally
    };

    const requestId = uuidv4();
    const startTime = new Date().toLocaleTimeString();
    setRequests((prev) => [...prev, { id: requestId, status: 'Pending', startTime }]);

    try {
      const response = await axios.post(apiUrl, requestData, {
        headers: { 'Content-Type': 'application/json' },
      });
      setRequests((prev) =>
        prev.map((req) =>
          req.id === requestId ? { ...req, status: 'Success' } : req
        )
      );
      console.log('Response:', response.data);
    } catch (error) {
      setRequests((prev) =>
        prev.map((req) =>
          req.id === requestId ? { ...req, status: 'Failed' } : req
        )
      );
      console.error('Error:', error);
    }
  };

  const handleRemoveAll = () => {
    setRequests([]);
  };

  // ===================== Rendering ===================== //
  return (
    <div>
      {/* ===== JSON Drive Fields ===== */}
      <div className="input-container">
        <label>JSON Drive ID</label>
        <input
          type="text"
          value={jsonDriveId}
          onChange={(e) => setJsonDriveId(e.target.value)}
        />
      </div>
      <div className="input-container">
        <label>JSON Folder ID</label>
        <input
          type="text"
          value={jsonFolderId}
          onChange={(e) => setJsonFolderId(e.target.value)}
          style={{ width: '300px' }}
        />
      </div>
      <button onClick={handleFetchJsonFiles}>
        Fetch JSON Files
      </button>
      <div className="input-container">
        <label>Available JSON Files</label>
        <select
          value={selectedJsonFileId}
          onChange={(e) => setSelectedJsonFileId(e.target.value)}
          style={{ width: '300px' }}
        >
          <option value="">-- Select JSON --</option>
          {availableJsonFiles.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>
      <hr />

      {/* ===== DOCX Drive Fields ===== */}
      <div className="input-container">
        <label>DOCX Drive ID</label>
        <input
          type="text"
          value={shared_drive_id}
          onChange={(e) => setshared_drive_id(e.target.value)}
        />
      </div>
      <div className="input-container">
        <label>DOCX Folder ID</label>
        <input
          type="text"
          value={template_folder_id}
          onChange={(e) => settemplate_folder_id(e.target.value)}
          style={{ width: '300px' }}
        />
      </div>
      <button onClick={handleFetchDocxFiles}>
        Fetch DOCX Files
      </button>

      {/* ===== Select a DOCX Template (only one allowed) ===== */}
      {!docxTemplate && (
        <div className="input-container">
          <label>Select DOCX Template (only one)</label>
          <select
            onChange={(e) => handleSelectDocxTemplate(e.target.value)}
            style={{ width: '300px' }}
          >
            <option value="">-- Select Template --</option>
            {availableDocxFiles.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ===== Display the Selected DOCX Template and its MongoDB References ===== */}
      {docxTemplate && (
        <div style={{ marginTop: '1rem', border: '1px solid #ccc', padding: '1rem' }}>
          <h4>Selected Template</h4>
          <p>
            <strong>File:</strong> {docxTemplate.fileName}
            <button onClick={handleRemoveTemplate} style={{ marginLeft: '1rem' }}>
              Remove Template
            </button>
          </p>
          <h4>MongoDB References</h4>
          {docxTemplate.references.length === 0 && <p>No references added yet.</p>}
          {docxTemplate.references.map((ref, index) => (
            <div key={index} style={{ marginBottom: '0.5rem' }}>
              <strong>Type:</strong> {ref.mongoType} <br />
              <strong>Value:</strong> {ref.mongoValue}
              <button onClick={() => handleRemoveReference(index)} style={{ marginLeft: '1rem' }}>
                Remove
              </button>
            </div>
          ))}
          <div style={{ marginTop: '1rem' }}>
            <label style={{ marginRight: '8px' }}>MongoDB Type:</label>
            <input
              type="text"
              value={mongoType}
              onChange={(e) => setMongoType(e.target.value)}
              placeholder="e.g. SSD MongoDB"
            />
            <br />
            <label style={{ marginRight: '8px' }}>MongoDB Value:</label>
            <input
              type="text"
              value={mongoValue}
              onChange={(e) => setMongoValue(e.target.value)}
              placeholder="state.collection1,collection2"
              style={{ width: '80%' }}
            />
            <br />
            <button onClick={handleAddReference} disabled={!mongoType}>
              Add Reference
            </button>
          </div>
        </div>
      )}

      <hr />

      {/* ===== Other Form Fields ===== */}
      <div className="input-container">
        <label>Link to Shapefile Folder</label>
        <input
          type="text"
          value={linkToFolder}
          onChange={(e) => setLinkToFolder(e.target.value)}
        />
      </div>
      <div className="input-container">
        <label>Output Drive Folder ID</label>
        <input
          type="text"
          value={output_drive_folder_id}
          onChange={(e) => setOutput_drive_folder_id(e.target.value)}
          style={{ width: '300px' }}
        />
      </div>
      <div className="input-container">
        <label>State</label>
        <input
          type="text"
          value={stateVal}
          onChange={(e) => setStateVal(e.target.value)}
        />
      </div>
      <div className="input-container">
        <label>Road Number</label>
        <input
          type="text"
          value={roadNumber}
          onChange={(e) => setRoadNumber(e.target.value)}
        />
      </div>
      <div className="input-container">
        <label>Start Mile</label>
        <input
          type="text"
          value={startMile}
          onChange={(e) => setStartMile(e.target.value)}
        />
      </div>
      <div className="input-container">
        <label>End Mile</label>
        <input
          type="text"
          value={endMile}
          onChange={(e) => setEndMile(e.target.value)}
        />
      </div>
      <div className="input-container">
        <label>Job Number</label>
        <input
          type="text"
          value={jobNumber}
          onChange={(e) => setJobNumber(e.target.value)}
        />
      </div>
      <div className="input-container">
        <label>Corridor</label>
        <input
          type="text"
          value={corridor}
          onChange={(e) => setCorridor(e.target.value)}
        />
      </div>
      <div className="input-container">
        <label>Path Collection(only for GRD)</label>
        <input
          type="text"
          value={pathCollection}
          onChange={(e) => setPathCollection(e.target.value)}
        />
      </div>

      <button
        className="run-button"
        onClick={handleRunAutomaticReports}
      >
        Run Automatic Reports
      </button>

      {/* ===== Requests Table ===== */}
      <div className="request-list">
        <table>
          <thead>
            <tr>
              <th>Request ID</th>
              <th>Status</th>
              <th>Start Time</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((req) => (
              <tr key={req.id}>
                <td>{req.id}</td>
                <td>{req.status}</td>
                <td>{req.startTime}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="remove-all-button" onClick={handleRemoveAll}>
          Remove All
        </button>
      </div>

    </div>
  );
};

export default AutomaticReportsTab;
